/**
 * SMS CONSENT — SINGLE SOURCE OF TRUTH  (A2P 10DLC / LeadConnector, 2026-10-01)
 *
 * Every form that can lead to a text message renders <SmsConsentCheckbox/>,
 * which shows EXACTLY this copy next to an UNCHECKED, OPTIONAL checkbox.
 *
 * The SAME version id + text is mirrored server-side in the `crm-capture`
 * edge function (SMS_DISCLOSURES). The server stores ITS canonical copy for the
 * version the browser reports, so the evidence row in mkt_consents can never
 * contain text the user was not actually shown.
 *
 * CHANGING THE WORDING? Bump SMS_CONSENT_VERSION and add the new text to the
 * crm-capture function as well. Old versions must stay there so historic
 * evidence rows keep resolving.
 */
export const SMS_CONSENT_VERSION = 'sms-v1-2026-10-01';

/** Program name used in the Terms / Privacy SMS sections and the carrier brief. */
export const SMS_PROGRAM_NAME = 'GIGGONE Alerts';

/** Support contact quoted in the HELP response and the Terms. */
export const SMS_SUPPORT_EMAIL = 'info@giggone.com';

export const SMS_CONSENT_TEXT: Record<'en' | 'es', string> = {
  en:
    'I agree to receive recurring automated service and marketing text messages from GIGGONE, including lead alerts, account updates, purchase notifications, and support messages. Consent is not a condition of purchase. Message frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for assistance. View our Privacy Policy and Terms of Service.',
  es:
    'Acepto recibir mensajes de texto automatizados y recurrentes de servicio y marketing de GIGGONE, incluyendo alertas de leads, actualizaciones de cuenta, notificaciones de compra y mensajes de soporte. El consentimiento no es una condición de compra. La frecuencia de los mensajes varía. Pueden aplicarse tarifas de mensajes y datos. Responda STOP para cancelar o HELP para obtener ayuda. Consulte nuestra Política de Privacidad y Términos de Servicio.',
};

/* =========================================================================
 * A2P 10DLC — CANONICAL PROGRAM CONFIGURATION (2026-10-01)
 * Must match the live site, the Trust Center submission and every SMS audit.
 * DO NOT change these URLs without updating the A2P registration documentation.
 * The disclosure text is also stored server-side in mkt_sms_disclosure_versions
 * (SMS eligibility requires a version that is valid there).
 * ========================================================================= */
export const A2P_URLS = {
  optIn: { en: 'https://giggone.com/en/sms-opt-in', es: 'https://giggone.com/es/mensajes-de-texto' },
  privacy: { en: 'https://giggone.com/en/privacy', es: 'https://giggone.com/es/privacidad' },
  terms: { en: 'https://giggone.com/en/terms', es: 'https://giggone.com/es/terminos' },
} as const;

/** Allowed message categories (A2P campaign use case). */
export const SMS_MESSAGE_TYPES: Record<'en' | 'es', string[]> = {
  en: ['Lead alerts', 'Account updates', 'Purchase confirmations', 'Credit notifications', 'Customer support communications'],
  es: ['Alertas de leads', 'Actualizaciones de cuenta', 'Confirmaciones de compra', 'Notificaciones de créditos', 'Comunicaciones de soporte al cliente'],
};

/** Official A2P opt-in flow description (Trust Center). */
export const A2P_OPT_IN_DESCRIPTION =
  'End users opt in by visiting https://giggone.com/en/sms-opt-in, entering their phone number, and voluntarily selecting an unchecked SMS consent checkbox. Consent is not a condition of purchase. Message frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for assistance. Users may receive lead alerts, account updates, purchase notifications, and support messages.';

/** Welcome / opt-in confirmation message (sent by the GHL workflow on the "GigGone SMS Opt In" tag). */
export const SMS_WELCOME_MESSAGE =
  'GIGGONE Alerts: You are subscribed to receive lead alerts, account updates, purchase notifications, and support messages. Message frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for assistance.';

export const A2P_SAMPLE_MESSAGES = {
  promotional: 'GIGGONE: A new lead matching your service area is available. Log in to giggone.com to view details and purchase access. Reply STOP to opt out.',
  transactional: 'GIGGONE: Your lead purchase has been completed successfully. Log in to giggone.com to view lead details. Reply STOP to opt out.',
};

/** SMS eligibility rule (enforced by the mkt_sms_eligible database view). */
export const SMS_ELIGIBILITY_RULE =
  'verification_status = verified AND excluded_from_sms = false AND a valid disclosure_version exists AND no later opt-out exists. Legacy records with no disclosure_version stay excluded until a new verified opt-in is collected.';

/** How the consent was captured — stored on every evidence row. */
export const SMS_CONSENT_METHOD = 'unchecked-checkbox-ticked';

/** Fields every form forwards to submitForm() when the box is ticked. */
export const smsConsentPayload = (checked: boolean) =>
  checked
    ? {
        smsOptIn: true as const,
        smsDisclosureVersion: SMS_CONSENT_VERSION,
        pageUrl: typeof window !== 'undefined' ? window.location.href.slice(0, 500) : undefined,
      }
    : { smsOptIn: false as const };
